/**
 * File Name          : outputset-packet.model.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { Packet } from "./packet.model";
import { Publisher } from "../publishers/publisher.model";
import { DATA_TYPE } from "../data-type.model";



export class OutputsetPacket extends Packet {
    mPktSubType: OUTPUTSET_TYPE | null = null;

    data = Array<OutputDatum>()

    static fromPacket(pkt: Packet, outputInfo: Publisher[]): [boolean, OutputsetPacket] {
        var thisPkt = new OutputsetPacket()

        thisPkt.mBytes = pkt.mBytes
        thisPkt.mPayloadLength = pkt.mPayloadLength
        thisPkt.mPacketNumber = pkt.mPacketNumber
        thisPkt.mPacketType = pkt.mPacketType

        thisPkt.rxCRC = pkt.rxCRC
        thisPkt.calcCRC = pkt.calcCRC
        thisPkt.validCRC = pkt.validCRC

        thisPkt.parseTypeHeader()

        var success = false;
        
        try {
            success = thisPkt.parseBody(outputInfo)
        } catch (error) {
            console.warn("Error parsing outputset packet: ", error)
            success = false
        }

        return [success, thisPkt]
    }

    parseTypeHeader() {
        this.mPktSubType = this.mBytes![5]
    }

    parseBody(outputInfo: Publisher[]): boolean {
        switch (this.mPktSubType) {
            case OUTPUTSET_TYPE.MIXED: {
                var index = 8
                var dv = new DataView(this.mBytes!.buffer)

                while(index + 5 < dv.byteLength) {
                    var pubIdx = dv.getUint8(index)
                    index++
                    var sampleTime_us = dv.getUint32(index, true)
                    index += 4

                    var values = []

                    var publisher = outputInfo.find((value)=>{return value.pubIdx == pubIdx})
                    if(publisher == null) {
                        return false
                    } else {
                        if(publisher.outputs == null) {
                            return false
                        } else {
                            for(let output of publisher.outputs) {
                                let [dataValue, offset]  = OutputsetPacket.getDatumValue(output.type!, output.length, index, dv)
                                index += offset
                                values.push(dataValue)
                            }

                            this.data.push(new OutputDatum(publisher.pubId, sampleTime_us, values))
                        }
                    }
                }
                


                break;
            }
            case OUTPUTSET_TYPE.SINGLE: {
                var index = 8
                var dv = new DataView(this.mBytes!.buffer)
                var pubIdx = dv.getUint8(index)
                index++
                var sampleTime_us = dv.getUint32(index, true)
                index += 4

                var values = []

                var publisher = outputInfo.find((value)=>{return value.pubIdx == pubIdx})
                if(publisher == null) {
                    return false
                } else {
                    if(publisher.outputs == null) {
                        return false
                    } else {
                        for(let output of publisher.outputs) {
                            let [dataValue, offset]  = OutputsetPacket.getDatumValue(output.type!, output.length, index, dv)
                            index += offset
                            values.push(dataValue)
                        }

                        this.data.push(new OutputDatum(publisher.pubId, sampleTime_us, values))
                    }
                }

                break;
            }
            case OUTPUTSET_TYPE.BUFFERED: {
                var index = 8
                var dv = new DataView(this.mBytes!.buffer)

                var pubIdx = dv.getUint8(index)
                var startTime = dv.getUint32(index += 1, true)
                var endTime = dv.getUint32(index += 4, true)
                var numSamples = dv.getUint16(index += 4, true)
                index +=2

                var sample = 0

                var publisher = outputInfo.find((value)=>{return value.pubIdx == pubIdx})
                if(publisher == null) {
                    return false
                } else {
                    if(publisher.outputs == null) {
                        return false
                    } else {
                        while((index<dv.byteLength) && (sample<numSamples)) {
                            var values = []

                            for(let output of publisher.outputs) {
                                let [dataValue, offset]  = OutputsetPacket.getDatumValue(output.type!, output.length, index, dv)
                                index += offset
                                values.push(dataValue)
                            }

                            this.data.push(new OutputDatum(publisher.pubId, startTime + sample*(endTime-startTime)/numSamples, values))

                            sample++
                        }
                    }
                }
                break;
            }

        }

        return true
    }

    static getDatumValue(dataType: DATA_TYPE, dataCount: number,  startIndex: number, dv: DataView): [(number|bigint)[], number] {
        let offset = 0
        let dataValue: (number|bigint)[] = []

        for(let count=0; count < dataCount; count++) {
            switch(dataType) {
                case DATA_TYPE.NA: {
                    break;
                }
                case DATA_TYPE.UINT8: {
                    dataValue.push((dv.getUint8(startIndex + offset)))
                    offset += 1
                    break;
                }
                case DATA_TYPE.UINT16: {
                    dataValue.push((dv.getUint16(startIndex + offset, true)))
                    offset += 2
                    break;
                }
                case DATA_TYPE.UINT32: {
                    dataValue.push((dv.getUint32(startIndex + offset, true)))
                    offset += 4
                    break;
                }
                case DATA_TYPE.UINT64: {
                    dataValue.push(dv.getBigUint64(startIndex + offset, true))
                    offset += 8
                    break;
                }
                case DATA_TYPE.INT8: {
                    dataValue.push((dv.getInt8(startIndex + offset)))
                    offset += 1
                    break;
                }
                case DATA_TYPE.INT16: {
                    dataValue.push((dv.getInt16(startIndex + offset, true)))
                    offset += 2
                    break;
                }
                case DATA_TYPE.INT32: {
                    dataValue.push((dv.getInt32(startIndex + offset, true)))
                    offset += 4
                    break;
                }
                case DATA_TYPE.INT64: {
                    dataValue.push(dv.getBigInt64(startIndex + offset, true))
                    offset += 8
                    break;
                }
                case DATA_TYPE.FLOAT32: {
                    dataValue.push(dv.getFloat32(startIndex + offset, true))
                    offset += 4
                    break;
                }
                case DATA_TYPE.FLOAT64: {
                    dataValue.push(dv.getFloat64(startIndex + offset, true))
                    offset += 8
                    break;
                }
                case DATA_TYPE.ENUM8: {
                    dataValue.push(dv.getUint8(startIndex + offset))
                    offset += 1
                    break;
                }
                default: {
                    break;
                }
            }
        }

        return [dataValue, offset]
    }

}

export class OutputDatum {
    pubId: number
    sampleTime_us: number;
    value: (number | bigint)[][];

    constructor(publisher: number, sampleTime_us: number, values:(number|bigint)[][]) {
        this.pubId = publisher
        this.sampleTime_us = sampleTime_us
        this.value = values
    }
}

export enum OUTPUTSET_TYPE {
    NA = 0x00,
    MIXED = 0x01,
    BUFFERED = 0x02,
    SINGLE = 0x03
}
